/**
 * 紧凑列表视图 - 主入口组件
 *
 * 专为OPS人员设计的高信息密度航班监控视图，类似Excel的紧凑布局：
 * - 更小的行高和字体
 * - 筛选条件以弹窗形式展示，节省屏幕空间
 * - 航班时间拆分为独立列（STD/STA/ETD/ETA/ATD/ATA）
 *
 * 架构说明：
 * - CompactFilter: 紧凑筛选组件（弹窗模式）
 * - columns/: 列定义模块（基础列、时间列、事务列）
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Table, Button, Tooltip, ConfigProvider, Divider, Modal, Checkbox } from "antd";
import { useMonitor } from "../../context/MonitorContext";
import { useTimeZone } from "../../../../../contexts/TimeZoneContext";
import { MODAL_KEYS } from "../../constants";
import { useTableSelection } from "../../../../../hooks";
import { useAuth } from "../../../../../hooks/useAuth";
import useFlightCopy, { DEFAULT_FLIGHT_COPY_COLUMNS, DEFAULT_FLIGHT_COPY_EXTRA_COLUMNS } from "../../hooks/useFlightCopy";
import BatchTransactionDrawer from "../../../BatchTransactionDrawer";
import EnhancedBatchActions from "../EnhancedBatchActions";
import CompactFilter from "./CompactFilter";
import { getBasicColumns, getCustomerColumn, getTimeColumns, getTransactionColumns } from "./columns";
import "./CompactListView.css";

const COMPACT_FIXED_OFFSET = 240; // 紧凑视图使用更小的偏移量

/**
 * 紧凑列表视图组件
 * 提供高信息密度的航班数据表格展示
 */
const CompactListView = ({ airportsMap = {} }) => {
    const { user } = useAuth();
    const { timeZone: timeZoneMode } = useTimeZone();
    const { flights, loading, pagination, updatePagination, toggleModal, filters, activeView, highlightFlightIds, setHighlightFlightIds, highlightedRowId, setHighlightedRowId, refreshAndHighlightFlight } = useMonitor();
    const [tableScrollY, setTableScrollY] = useState(0);
    const [batchTransactionDrawerVisible, setBatchTransactionDrawerVisible] = useState(false);
    const [currentBatchTransactionType, setCurrentBatchTransactionType] = useState(null);

    // 使用useMemo缓存表格数据
    const tableDataSource = useMemo(() => {
        return flights || [];
    }, [flights]);

    const { selectedRowKeys, tableRef, rowSelection, getIndexColumn, getRowProps, clearSelection, handleRowSelectionChange } = useTableSelection({
        rowKey: "id",
        dataSource: tableDataSource,
        pagination,
        mouseMoveThrottle: 120,
    });

    const { copyConfigModalVisible, selectedCopyColumns, extendedCopyColumns, handleCopyFlights, handleCloseCopyConfig, handleCopyColumnChange, copyMenuItems } = useFlightCopy({
        defaultColumns: DEFAULT_FLIGHT_COPY_COLUMNS,
        extendedColumns: DEFAULT_FLIGHT_COPY_EXTRA_COLUMNS,
        copyTitle: "AAE SCHEDULE (UTC TIME)",
        flights: tableDataSource,
        selectedRowKeys,
        airportsMap,
    });

    // ==================== 事件处理函数 ====================

    // 全选当前页
    const handleSelectAllVisible = useCallback(() => {
        const visibleKeys = tableDataSource.map((item) => item.id);
        handleRowSelectionChange(visibleKeys, tableDataSource);
    }, [tableDataSource, handleRowSelectionChange]);

    // 移除单个航班
    const handleRemoveFlight = useCallback(
        (flightId) => {
            const newSelectedKeys = selectedRowKeys.filter((key) => key !== flightId);
            const newSelectedRows = tableDataSource.filter((flight) => newSelectedKeys.includes(flight.id));
            handleRowSelectionChange(newSelectedKeys, newSelectedRows);
        },
        [selectedRowKeys, tableDataSource, handleRowSelectionChange],
    );

    // 航班编辑成功回调
    const handleFlightInfoEditSuccess = useCallback(
        (flightId, responseData) => {
            refreshAndHighlightFlight(flightId, responseData);
        },
        [refreshAndHighlightFlight],
    );

    // 批量事务抽屉
    const handleOpenBatchTransactionDrawer = useCallback((transactionType) => {
        setCurrentBatchTransactionType(transactionType);
        setBatchTransactionDrawerVisible(true);
    }, []);

    const handleCloseBatchTransactionDrawer = useCallback(() => {
        setBatchTransactionDrawerVisible(false);
        setCurrentBatchTransactionType(null);
    }, []);

    const handleBatchTransactionSuccess = useCallback(
        (result) => {
            if (result.changesSummary) {
                selectedRowKeys.forEach((flightId) => refreshAndHighlightFlight(flightId));
            } else if (result.results) {
                result.results.forEach((item) => {
                    if (item.success) refreshAndHighlightFlight(item.flight_id);
                });
            } else {
                selectedRowKeys.forEach((flightId) => refreshAndHighlightFlight(flightId));
            }
        },
        [refreshAndHighlightFlight, selectedRowKeys],
    );

    // 判断航班告警
    const hasWarnings = useCallback((flight) => flight.status === "DELAYED", []);

    // 显示详情
    const handleShowDetails = useCallback((flight) => toggleModal(MODAL_KEYS.FLIGHT_DETAIL, true, flight), [toggleModal]);

    // 分页变化
    const handleTableChange = useCallback((pag) => updatePagination({ current: pag.current, pageSize: pag.pageSize }), [updatePagination]);

    // 行类名 - 包含rotation分组分隔
    const getRowClassName = useCallback(
        (record, index) => {
            let className = "compact-row";
            if (highlightedRowId.includes(record.id)) className += " row-updated";

            // Rotation分组判断
            // 逻辑: 如果当前航班属于某个Rotation，且 (是最后一个 或 下一个航班属于不同Rotation)
            const nextFlight = tableDataSource[index + 1];
            if (record.rotation_no && (!nextFlight || record.rotation_no !== nextFlight.rotation_no)) {
                className += " rotation-end";
            }

            return className;
        },
        [highlightedRowId, tableDataSource],
    );

    // 自定义行属性
    const getCustomRowProps = useCallback(
        (record, index) => {
            const { onClick, ...restProps } = getRowProps(record, index);
            return restProps;
        },
        [getRowProps],
    );

    // ==================== 副作用 ====================

    // 表格高度计算
    useEffect(() => {
        const updateTableHeight = () => {
            const calculatedHeight = window.innerHeight - COMPACT_FIXED_OFFSET - 15; // 预留横向滚动条高度，防止遮挡最后一行
            setTableScrollY(calculatedHeight > 0 ? calculatedHeight : 400);
        };
        updateTableHeight();
        window.addEventListener("resize", updateTableHeight);
        return () => window.removeEventListener("resize", updateTableHeight);
    }, []);

    useEffect(() => {
        if (highlightFlightIds.length > 0 && !loading && flights.length > 0) {
            const foundIds = highlightFlightIds.filter((id) => flights.some((f) => f.id === id));
            if (foundIds.length > 0) {
                setHighlightedRowId(foundIds);
                setHighlightFlightIds([]);
                requestAnimationFrame(() => {
                    const row = document.querySelector(`[data-row-key="${foundIds[0]}"]`);
                    if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
                });
            }
        }
    }, [highlightFlightIds, loading, flights, setHighlightFlightIds, setHighlightedRowId]);

    // 筛选变化清空选择 + 清除高亮
    const prevFiltersRef = useRef(JSON.stringify(filters));
    const prevActiveViewRef = useRef(activeView);
    useEffect(() => {
        const currentFilters = JSON.stringify(filters);
        if (currentFilters !== prevFiltersRef.current || activeView !== prevActiveViewRef.current) {
            clearSelection();
            setHighlightedRowId([]);
            prevFiltersRef.current = currentFilters;
            prevActiveViewRef.current = activeView;
        }
    }, [filters, activeView, clearSelection, setHighlightedRowId]);

    // ==================== 列定义（使用模块化构建） ====================

    const columns = useMemo(() => {
        // 基础列
        const basicCols = getBasicColumns({
            getIndexColumn,
            timeZoneMode,
            airportsMap,
            hasWarnings,
            handleShowDetails,
            user,
            tableDataSource,
        });

        // 时间列
        const timeCols = getTimeColumns({
            airportsMap,
            tableDataSource,
            handleFlightInfoEditSuccess,
        });

        // 备注列
        const remarksCol = {
            title: "备注",
            dataIndex: "remarks",
            key: "remarks",
            width: 100,
            ellipsis: true,
            render: (text) => {
                if (!text) return "-";
                return (
                    <Tooltip title={text}>
                        <span style={{ fontSize: "10px" }}>{text.length > 12 ? text.substring(0, 12) + "..." : text}</span>
                    </Tooltip>
                );
            },
        };

        // 事务列
        const transactionCols = getTransactionColumns({
            user,
            refreshAndHighlightFlight,
        });

        // 客户列（只读，按 sales:flight-attribution:customer-view 门控；无权限返回空数组）
        const customerCols = getCustomerColumn({ user, width: 156 });

        return [...basicCols, ...timeCols, remarksCol, ...customerCols, ...transactionCols];
    }, [getIndexColumn, timeZoneMode, airportsMap, hasWarnings, handleShowDetails, user, tableDataSource, handleFlightInfoEditSuccess, refreshAndHighlightFlight]);

    // ==================== 渲染 ====================

    return (
        <div className="compact-list-view" style={{ position: "relative" }}>
            {/* 紧凑筛选条 */}
            <CompactFilter />

            {/* 批量操作栏 - sticky 定位确保滚动时可见 */}
            {selectedRowKeys.length > 0 && (
                <div className="batch-actions-sticky-wrapper">
                    <EnhancedBatchActions selectedFlightIds={selectedRowKeys} onSuccess={refreshAndHighlightFlight} allFlightsData={tableDataSource} onOpenBatchTransactionDrawer={handleOpenBatchTransactionDrawer} onFlightRemove={handleRemoveFlight} onSelectAll={handleSelectAllVisible} onClearSelection={clearSelection} copyMenuItems={copyMenuItems} />
                </div>
            )}

            {/* 紧凑表格 */}
            <ConfigProvider
                theme={{
                    components: {
                        Table: {
                            cellPaddingBlock: 2,
                            cellPaddingBlockSM: 2,
                            cellPaddingInline: 4,
                            cellPaddingInlineSM: 4,
                        },
                    },
                }}
            >
                <div className="compact-table-wrapper">
                    <Table
                        ref={tableRef}
                        dataSource={tableDataSource}
                        columns={columns}
                        loading={loading}
                        rowKey="id"
                        size="small"
                        scroll={{ x: 960, y: tableScrollY, scrollToFirstRowOnChange: false }}
                        virtual={{ itemHeight: 24 }}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            pageSizeOptions: [50, 100, 200, 500],
                            showQuickJumper: true,
                            size: "small",
                            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
                        }}
                        onChange={handleTableChange}
                        onRow={getCustomRowProps}
                        rowSelection={{ ...rowSelection, columnWidth: 4 }}
                        rowClassName={getRowClassName}
                    />
                </div>
            </ConfigProvider>

            {/* 批量事务抽屉 */}
            <BatchTransactionDrawer key={currentBatchTransactionType} visible={batchTransactionDrawerVisible} onClose={handleCloseBatchTransactionDrawer} selectedFlights={tableDataSource.filter((flight) => selectedRowKeys.includes(flight.id))} transactionType={currentBatchTransactionType} onSuccess={handleBatchTransactionSuccess} />
            {/* 复制配置弹窗 */}
            <Modal
                title="自定义复制列"
                open={copyConfigModalVisible}
                onCancel={handleCloseCopyConfig}
                onOk={() => {
                    handleCopyFlights(true);
                    handleCloseCopyConfig();
                }}
                okText="复制选中列"
                cancelText="取消"
                width={600}
            >
                <div style={{ marginBottom: 16 }}>
                    <p>选择要复制的列（已选择 {selectedRowKeys.length} 个航班）：</p>
                </div>
                <Checkbox.Group value={selectedCopyColumns} onChange={handleCopyColumnChange} style={{ width: "100%" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                        {extendedCopyColumns.map((column) => (
                            <Checkbox key={column.key} value={column.key}>
                                {column.label}
                            </Checkbox>
                        ))}
                    </div>
                </Checkbox.Group>
            </Modal>
        </div>
    );
};

export default CompactListView;
